/**
 * Platform admin card: clear the Stripe price-ID cache after editing prices
 * in Stripe (POST /api/billing/admin/refresh-price-cache).
 */
import React, { useState } from 'react';
import { ActivityIndicator } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { AdminCalloutBanner, AdminSectionCard, ADMIN_COLORS } from '@/components/admin/AdminUI';
import { apiRequest } from '../../services/apiClient';

export default function StripePriceCacheCard() {
  const [priceCacheLoading, setPriceCacheLoading] = useState(false);
  const [priceCacheResult, setPriceCacheResult] = useState<{
    cleared_entries: number;
    message: string;
  } | null>(null);

  const handleRefreshPriceCache = async () => {
    if (priceCacheLoading) return;
    setPriceCacheLoading(true);
    setPriceCacheResult(null);
    try {
      const data = await apiRequest<{ status: string; cleared_entries: number; message: string }>({
        endpoint: '/billing/admin/refresh-price-cache',
        method: 'POST',
      });
      setPriceCacheResult({
        cleared_entries: data.cleared_entries ?? 0,
        message: data.message || 'Cache cleared.',
      });
    } catch (err: any) {
      setPriceCacheResult({
        cleared_entries: 0,
        message: err?.message || 'Failed to refresh price cache.',
      });
    } finally {
      setPriceCacheLoading(false);
    }
  };

  return (
    <AdminSectionCard
      icon="local-offer"
      title="Stripe Price Cache"
      subtitle="Prices resolve by lookup_key with a 1h cache. Refresh after editing prices in Stripe (or running setup_stripe_products.py) so the next request refetches."
    >
      <Button
        variant="outline"
        disabled={priceCacheLoading}
        onPress={handleRefreshPriceCache}
        className="flex-row items-center justify-center gap-2"
      >
        {priceCacheLoading ? (
          <ActivityIndicator size="small" color={ADMIN_COLORS.text.primary} />
        ) : (
          <MaterialIcons name="refresh" size={18} color={ADMIN_COLORS.text.primary} />
        )}
        <Text
          className="text-sm font-semibold"
          style={{ color: ADMIN_COLORS.text.primary }}
        >
          Refresh Price Cache
        </Text>
      </Button>
      {priceCacheResult ? (
        <AdminCalloutBanner
          tone={priceCacheResult.cleared_entries > 0 ? 'success' : 'info'}
          body={`${priceCacheResult.message} Cleared ${priceCacheResult.cleared_entries} cached price IDs.`}
        />
      ) : null}
    </AdminSectionCard>
  );
}
